import { Link } from "react-router-dom";
import { ShieldCheck, Cookie, Mail } from "lucide-react";
import { school } from "../mock/mock";
import useSeo from "../hooks/useSeo";

const openSettings = () => window.dispatchEvent(new Event("ct-open-cookie-settings"));

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-40">
    <h2 className="font-display text-2xl font-bold text-[#0B2545] mt-12 mb-4">{title}</h2>
    <div className="space-y-3 text-slate-700 leading-relaxed">{children}</div>
  </section>
);

export default function Integritetspolicy() {
  useSeo({ title: "Integritetspolicy & cookies", description: "Så behandlar City Trafikskola Uppsala AB dina personuppgifter enligt GDPR och hur vi använder cookies på webbplatsen." });

  return (
    <main className="pt-20 md:pt-[8.25rem]" data-testid="privacy-page">
      <section className="bg-[#0B2545] py-16">
        <div className="max-w-4xl mx-auto px-5">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ffce3a]/15 border border-[#ffce3a]/40 text-[#ffce3a] font-bold tracking-[0.2em] text-xs uppercase"><ShieldCheck size={14} /> GDPR</span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white mt-5">Integritetspolicy & cookies</h1>
          <p className="text-slate-300 mt-4">Senast uppdaterad: 1 juni 2026</p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-5 py-12">
        <p className="text-lg text-slate-700 leading-relaxed">
          City Trafikskola Uppsala AB värnar om din personliga integritet. Här beskriver vi hur vi behandlar personuppgifter i enlighet med EU:s dataskyddsförordning (GDPR, förordning (EU) 2016/679) och kompletterande svensk lagstiftning, samt hur vi använder cookies enligt lagen om elektronisk kommunikation (LEK).
        </p>

        <Section id="ansvarig" title="1. Personuppgiftsansvarig">
          <p>City Trafikskola Uppsala AB, org.nr 559426-4870, {school.address}, {school.postal}, är personuppgiftsansvarig för behandlingen av dina personuppgifter.</p>
          <p>Kontakt i dataskyddsfrågor: <a href={`mailto:${school.email}`} className="font-semibold text-[#1B6CA8] underline underline-offset-2">{school.email}</a>, telefon 018-265 06 46.</p>
        </Section>

        <Section id="uppgifter" title="2. Vilka personuppgifter vi behandlar">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Kontaktuppgifter</strong> – namn, e-postadress och telefonnummer som du lämnar via kontaktformuläret, e-post eller telefon.</li>
            <li><strong>Elevuppgifter</strong> – personnummer, adress, körkortstillstånd, utbildningsplan, genomförda lektioner, kurser och provresultat när du är inskriven elev.</li>
            <li><strong>Betalningsuppgifter</strong> – uppgifter om köp, fakturor och betalningar (Swish, kort, Qliro). Kortuppgifter hanteras av betalleverantören och lagras inte hos oss.</li>
            <li><strong>Tekniska uppgifter</strong> – ett slumpmässigt besökar-ID, webbläsare, enhetstyp, språk, skärmstorlek, trafikkälla, besökta sidor och klick på knappar (endast med ditt samtycke till statistikcookies). Startsidans film laddas via youtube-nocookie.com.</li>
          </ul>
        </Section>

        <Section id="andamal" title="3. Ändamål och rättslig grund">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
              <thead className="bg-[#f6f9fc] text-[#0B2545]">
                <tr><th className="text-left p-3 font-bold">Ändamål</th><th className="text-left p-3 font-bold">Rättslig grund</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="p-3">Svara på frågor via kontaktformulär, e-post och telefon</td><td className="p-3">Berättigat intresse (art. 6.1 f)</td></tr>
                <tr><td className="p-3">Genomföra och administrera din körkortsutbildning, bokningar och betalningar</td><td className="p-3">Avtal (art. 6.1 b)</td></tr>
                <tr><td className="p-3">Rapportera genomförd riskutbildning och utbildning till Transportstyrelsen/Trafikverket</td><td className="p-3">Rättslig förpliktelse (art. 6.1 c)</td></tr>
                <tr><td className="p-3">Bokföring och redovisning</td><td className="p-3">Rättslig förpliktelse (bokföringslagen)</td></tr>
                <tr><td className="p-3">Besöksstatistik på webbplatsen</td><td className="p-3">Samtycke (art. 6.1 a) via cookiebannern</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section id="lagring" title="4. Hur länge vi sparar uppgifterna">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Meddelanden via kontaktformuläret sparas i högst 12 månader efter att ärendet avslutats.</li>
            <li>Elevuppgifter sparas under utbildningstiden och därefter så länge som krävs enligt Transportstyrelsens föreskrifter om utbildningsdokumentation.</li>
            <li>Bokföringsunderlag sparas i 7 år enligt bokföringslagen.</li>
            <li>Cookiesamtycke sparas i din webbläsare i 12 månader.</li>
          </ul>
        </Section>

        <Section id="delning" title="5. Vem vi delar uppgifter med">
          <p>Vi säljer aldrig dina personuppgifter. Uppgifter delas endast med:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Teoricentralen</strong> – leverantör av vår elevportal, teorimaterial och bokningssystem (personuppgiftsbiträde inom EU/EES).</li>
            <li><strong>Transportstyrelsen och Trafikverket</strong> – lagstadgad rapportering av utbildning och riskutbildning.</li>
            <li><strong>Betalleverantörer</strong> (Swish, Qliro, kortinlösare) – för att genomföra betalningar.</li>
            <li><strong>IT- och driftleverantörer</strong> för webbplats och e-post, bundna av personuppgiftsbiträdesavtal.</li>
          </ul>
          <p>Om uppgifter överförs utanför EU/EES (t.ex. vid användning av Google/YouTube-tjänster) sker det med stöd av EU-kommissionens standardavtalsklausuler eller motsvarande skyddsåtgärder.</p>
        </Section>

        <Section id="rattigheter" title="6. Dina rättigheter">
          <p>Du har rätt att:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>få tillgång till de personuppgifter vi behandlar om dig (registerutdrag),</li>
            <li>få felaktiga uppgifter rättade och, under vissa förutsättningar, raderade,</li>
            <li>begära begränsning av behandlingen och invända mot behandling som grundas på berättigat intresse,</li>
            <li>få ut dina uppgifter i ett maskinläsbart format (dataportabilitet),</li>
            <li>när som helst återkalla ett lämnat samtycke, t.ex. för cookies.</li>
          </ul>
          <p>Kontakta oss på <a href={`mailto:${school.email}`} className="font-semibold text-[#1B6CA8] underline underline-offset-2">{school.email}</a> för att utöva dina rättigheter. Du har också rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY), <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1B6CA8] underline underline-offset-2">www.imy.se</a>.</p>
        </Section>

        <Section id="cookies" title="7. Cookies">
          <p>Cookies är små textfiler som lagras i din webbläsare. Vi använder följande kategorier:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
              <thead className="bg-[#f6f9fc] text-[#0B2545]">
                <tr><th className="text-left p-3 font-bold">Kategori</th><th className="text-left p-3 font-bold">Syfte</th><th className="text-left p-3 font-bold">Lagringstid</th><th className="text-left p-3 font-bold">Kräver samtycke</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="p-3 font-semibold">Nödvändiga</td><td className="p-3">Kommer ihåg ditt cookieval och gör att sidan fungerar.</td><td className="p-3">12 månader</td><td className="p-3">Nej</td></tr>
                <tr><td className="p-3 font-semibold">Statistik</td><td className="p-3">Besöksstatistik (sidvisningar, enhet, webbläsare, trafikkälla, klick på knappar) för att förbättra webbplatsen. Lagras i vårt eget system – delas inte med tredje part.</td><td className="p-3">Upp till 12 månader</td><td className="p-3">Ja</td></tr>
              </tbody>
            </table>
          </div>
          <p>Inga icke-nödvändiga cookies sätts innan du lämnat ditt samtycke. Du kan när som helst ändra eller återkalla ditt val:</p>
          <button onClick={openSettings} data-testid="privacy-cookie-settings-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B2545] text-white font-bold hover:bg-[#13315C] transition-colors">
            <Cookie size={17} /> Ändra cookie-inställningar
          </button>
          <p className="text-sm">Du kan även blockera eller radera cookies i webbläsarens inställningar. Vissa funktioner kan då sluta fungera.</p>
        </Section>

        <Section id="sakerhet" title="8. Säkerhet">
          <p>Vi vidtar lämpliga tekniska och organisatoriska åtgärder för att skydda dina uppgifter, bland annat krypterad överföring (HTTPS), behörighetsstyrning och lösenordsskyddade system. Endast personal som behöver uppgifterna för sitt arbete har åtkomst.</p>
        </Section>

        <Section id="andringar" title="9. Ändringar i policyn">
          <p>Vi kan komma att uppdatera denna policy. Den senaste versionen finns alltid publicerad på denna sida med datum för senaste ändring.</p>
        </Section>

        <div className="mt-14 rounded-3xl bg-[#fff8e1] border border-[#ffce3a]/50 p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div>
            <h3 className="font-display text-xl font-bold text-[#0B2545]">Frågor om dina personuppgifter?</h3>
            <p className="text-slate-600 mt-1">Hör av dig – vi svarar normalt inom några arbetsdagar.</p>
          </div>
          <a href={`mailto:${school.email}`} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#0B2545] text-white font-bold hover:bg-[#13315C] transition-colors whitespace-nowrap">
            <Mail size={17} /> {school.email}
          </a>
        </div>
        <p className="mt-8 text-sm text-slate-500">Tillbaka till <Link to="/" className="font-semibold text-[#1B6CA8] underline underline-offset-2">startsidan</Link>.</p>
      </article>
    </main>
  );
}
